import React, { useRef, useEffect } from 'react';
import { useInView } from '../../hooks/useInView';

interface WaypointProps {
  onEnter?: () => void;
  onLeave?: () => void;
  topOffset?: string | number;
  children: React.ReactNode;
}

export const Waypoint: React.FC<WaypointProps> = ({ onEnter, onLeave, topOffset, children }) => {
  const childElementRef = useRef<HTMLElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Find and observe the first actual child element
  useEffect(() => {
    if (wrapperRef.current) {
      // Find first actual element child (skip text nodes)
      const firstElement = Array.from(wrapperRef.current.children).find((child) => child instanceof HTMLElement) as
        | HTMLElement
        | undefined;
      childElementRef.current = firstElement || null;
    }
  });

  const isInView = useInView(childElementRef, { topOffset, triggerOnMount: true });
  const previousInViewRef = useRef<boolean | null>(null);

  useEffect(() => {
    // Only trigger callbacks when state changes (entering or leaving)
    if (previousInViewRef.current === null) {
      // First render - set initial state
      previousInViewRef.current = isInView;
      if (isInView && onEnter) {
        onEnter();
      }
      return;
    }

    if (isInView && !previousInViewRef.current && onEnter) {
      onEnter();
    } else if (!isInView && previousInViewRef.current && onLeave) {
      onLeave();
    }

    previousInViewRef.current = isInView;
  }, [isInView, onEnter, onLeave]);

  return (
    <div ref={wrapperRef} style={{ display: 'contents' }}>
      {children}
    </div>
  );
};
